import React, {useState, useRef, useEffect} from 'react';
import {MenuItem} from '@carbon/react';
import {ChevronRight} from '@carbon/icons-react';
import './menu-item-with-submenu.scss';

export interface MenuItemWithSubmenuProps {
    label: string;
    disabled?: boolean;
    alignment?: 'left' | 'right';
    submenuItems: Array<{
        label: string;
        onClick: () => void;
        disabled?: boolean;
        renderIcon?: React.ComponentType;
    }>;
}

const MenuItemWithSubmenu = (props: MenuItemWithSubmenuProps) => {
    const {label, disabled = false, alignment = 'right', submenuItems} = props;
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
    const openTimerRef = useRef<NodeJS.Timeout | null>(null);
    const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (disabled) return;

        // Cancel any pending close timer
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }

        // Open submenu after 100ms delay
        openTimerRef.current = setTimeout(() => {
            setIsSubmenuOpen(true);
        }, 100);
    };

    const handleMouseLeave = () => {
        if (disabled) return;

        // Cancel any pending open timer
        if (openTimerRef.current) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }

        // Close submenu after 200ms delay
        closeTimerRef.current = setTimeout(() => {
            setIsSubmenuOpen(false);
        }, 200);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (disabled) return;

        switch (event.key) {
        case 'ArrowRight':
            event.preventDefault();
            setIsSubmenuOpen(true);
            break;
        case 'ArrowLeft':
            event.preventDefault();
            setIsSubmenuOpen(false);
            break;
        case 'Escape':
            event.preventDefault();
            setIsSubmenuOpen(false);
            break;
        default:
            break;
        }
    };

    const handleSubmenuItemClick = (onClick: () => void) => {
        onClick();
        setIsSubmenuOpen(false);
    };

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (openTimerRef.current) clearTimeout(openTimerRef.current);
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, []);

    return (
        <div
            className={`menu-item-with-submenu${disabled ? ' disabled' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            role='menuitem'
            aria-haspopup='true'
            aria-expanded={isSubmenuOpen}
            tabIndex={disabled ? -1 : 0}
        >
            <MenuItem
                label={label}
                disabled={disabled}
                renderIcon={ChevronRight}
            />
            {isSubmenuOpen && (
                <div
                    className={`submenu open align-${alignment}`}
                    role='menu'
                >
                    {submenuItems.map((item) => (
                        <MenuItem
                            key={item.label}
                            label={item.label}
                            onClick={() => handleSubmenuItemClick(item.onClick)}
                            disabled={item.disabled}
                            renderIcon={item.renderIcon as any}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MenuItemWithSubmenu;
